<template>
  <div class="bid-activity" data-testid="bid-activity-chart">
    <v-row v-if="initialLoad" class="my-12" justify="center" align="center">
      <v-progress-circular indeterminate color="primary"/>
    </v-row>

    <template v-else>
      <div v-show="totalTracts === 0" style="height: 160px">
        <NoData class="fill-height">{{ $t('noData') }}</NoData>
      </div>

      <v-row v-show="totalTracts !== 0" dense>
        <v-col cols="12" md="3">
          <DetailCard :title="$t('summary')">
            <template #title.adjacent>
              <Icon
              :icon="IconHelpers.getIconForObjectType('tract')"
              iconColor="primary"
              :small="false"
              tabindex="0"
              :tooltipText="$t('tracts')"
              elementId="bid-tract-stats-icon"
              @icon-clicked="$emit('open-tracts')"/>
              <Icon
              :icon="IconHelpers.getIconForObjectType('account')"
              iconColor="primary"
              :small="false"
              tabindex="0"
              :tooltipText="$t('suppliers')"
              elementId="bid-supplier-stats-icon"
              @icon-clicked="$emit('open-suppliers')"/>
            </template>
            <v-row no-gutters v-for="row in stateRows" :key="row.key">
              <v-col>{{ row.label }}:</v-col>
              <v-col cols="4"><span class="text--secondary">{{ row.value }}</span></v-col>
            </v-row>
          </DetailCard>
        </v-col>
        <v-col cols="12" md="9" class="d-flex">
          <div v-show="trendHasData" class="px-4 flex-grow-1" style="position: relative; min-height: 300px; min-width: 0">
            <canvas ref="trendCanvas"/>
          </div>
          <div v-show="!trendHasData" class="px-4 flex-grow-1" style="min-height: 300px; min-width: 0">
            <NoData class="fill-height">{{ $t('noData') }}</NoData>
          </div>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
import moment from 'moment'
import { mapActions } from 'vuex'
import { Chart } from 'chart.js'
import { chartTooltipStyle } from '@/utils/ChartHelpers.js'
import { companyOffsetToLocalDate } from '@/utils/DateFormatter.js'
import IconHelpers from '@/utils/IconHelpers'
import { theme, helpers } from '@/plugins/theme.js'

const WEEKLY_BUCKET_THRESHOLD_DAYS = 9 * 7

const CHART_FONT = { family: 'Roboto, sans-serif' }
const CHART_TICK_COLOR = 'rgba(0, 0, 0, 0.6)'

export default {
  name: 'BidActivityChart',

  components: {
    NoData: () => import('@/components/core/NoData.vue'),
    DetailCard: () => import('@/components/core/DetailCard.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    statisticsParams: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },

  data: () => ({
    IconHelpers,
    loadingStatistics: false,
    statistics: undefined,
    statisticsRequestId: 0
  }),

  watch: {
    statisticsParams () {
      this.loadStatistics()
    },

    trend () {
      this.drawTrend()
    }
  },

  computed: {
    initialLoad () {
      return this.loadingStatistics && !this.statistics
    },

    totalTracts () {
      if (!this.statistics) return 0
      const s = this.statistics
      return s.open + s.won + s.lost
    },

    trendHasData () {
      return (this.statistics?.dailyCounts ?? []).length > 0
    },

    trend () {
      if (!this.statistics) return { labels: [], datasets: [] }

      // from/to are company-offset instants -> shift to company-local days; dc.date already arrives as the bid's calendar day
      const windowFrom = moment.utc(companyOffsetToLocalDate(this.statistics.from, 'YYYY-MM-DD'))
      const windowTo = moment.utc(companyOffsetToLocalDate(this.statistics.to, 'YYYY-MM-DD')).endOf('day')
      const daily = windowTo.diff(windowFrom, 'days') < WEEKLY_BUCKET_THRESHOLD_DAYS
      const startOfBucket = (date) => daily ? date.clone().startOf('day') : date.clone().startOf('isoWeek')

      const buckets = []
      const bucketIndexByKey = {}
      const cursor = startOfBucket(windowFrom)
      while (cursor.isSameOrBefore(windowTo)) {
        bucketIndexByKey[cursor.format('YYYY-MM-DD')] = buckets.length
        buckets.push({ label: cursor.format('MMM D'), won: 0, lost: 0 })
        cursor.add(1, daily ? 'day' : 'week')
      }

      const dailyCounts = this.statistics.dailyCounts ?? []
      dailyCounts.forEach(dc => {
        const bucket = buckets[bucketIndexByKey[startOfBucket(moment.utc(dc.date)).format('YYYY-MM-DD')]]
        if (!bucket) return
        bucket.won += dc.won
        bucket.lost += dc.lost
      })

      const area = (color) => ({
        borderColor: color,
        backgroundColor: helpers.getTranslucent(20, color),
        fill: true,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 3
      })

      // Dataset order sets the stacking: lost fills the bottom band, won sits above it.
      // Won fills to the lost line ('-1'), not the origin — an origin fill would tint the lost band through the translucency.
      return {
        labels: buckets.map(b => b.label),
        datasets: [
          { label: this.$t('lost'), data: buckets.map(b => b.lost), ...area(theme.error) },
          { label: this.$t('won'), data: buckets.map(b => b.won), ...area(theme.success), fill: '-1' }
        ]
      }
    },

    stateRows () {
      const s = this.statistics ?? { open: 0, won: 0, lost: 0 }
      const decided = s.won + s.lost
      const winRate = decided > 0 ? `${Math.round((s.won / decided) * 100)}%` : '—'
      return [
        { key: 'open', label: this.$t('open'), value: s.open },
        { key: 'won', label: this.$t('won'), value: s.won },
        { key: 'lost', label: this.$t('lost'), value: s.lost },
        { key: 'winRate', label: this.$t('winRate'), value: winRate }
      ]
    }
  },

  created () {
    this.loadStatistics()
  },

  beforeDestroy () {
    const canvas = this.$refs.trendCanvas
    const existingChart = canvas ? Chart.getChart(canvas) : undefined
    if (existingChart) existingChart.destroy()
  },

  methods: {
    ...mapActions('bid', ['fetchBidStatistics']),

    async loadStatistics () {
      const requestId = ++this.statisticsRequestId
      this.loadingStatistics = true
      try {
        const statistics = await this.fetchBidStatistics(this.statisticsParams)
        if (requestId === this.statisticsRequestId) this.statistics = statistics
      } finally {
        if (requestId === this.statisticsRequestId) this.loadingStatistics = false
      }
    },

    drawTrend () {
      this.$nextTick(() => {
        const canvas = this.$refs.trendCanvas
        if (!canvas) return
        const existingChart = Chart.getChart(canvas)
        if (existingChart) existingChart.destroy()
        return new Chart(canvas, {
          type: 'line',
          data: this.trend,
          options: {
            maintainAspectRatio: false,
            responsive: true,
            animation: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
              legend: {
                position: 'bottom',
                labels: { usePointStyle: true, boxWidth: 8, boxHeight: 8, color: CHART_TICK_COLOR, font: CHART_FONT }
              },
              tooltip: {
                callbacks: {
                  label: (item) => {
                    const total = item.chart.data.datasets.reduce((sum, dataset) => sum + (dataset.data[item.dataIndex] || 0), 0)
                    const percent = total ? Math.round((item.parsed.y / total) * 100) : 0
                    return `${item.dataset.label}: ${item.parsed.y} (${percent}%)`
                  }
                },
                ...chartTooltipStyle
              }
            },
            scales: {
              x: {
                stacked: true,
                grid: { display: false, drawBorder: false },
                ticks: { color: CHART_TICK_COLOR, font: CHART_FONT, maxRotation: 0, autoSkipPadding: 12 }
              },
              y: {
                stacked: true,
                beginAtZero: true,
                grid: { color: 'rgba(0, 0, 0, 0.06)', drawBorder: false },
                ticks: { precision: 0, color: CHART_TICK_COLOR, font: CHART_FONT }
              }
            }
          }
        })
      })
    }
  }
}
</script>
